import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Receipt, ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const justRegistered = params.get("inscription") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" || u.role === "staff" ? "/tableau-de-bord" : u.role === "supplier" ? "/portail-fournisseur" : "/portail");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background text-left">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between bg-[#0a0a0a] text-white p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "42px 42px" }}
        />
        <div
          className="absolute -top-24 -right-24 h-80 w-80 rounded-full blur-3xl opacity-30"
          style={{ background: "radial-gradient(circle, #FF6B35, transparent 70%)" }}
        />
        <div className="relative">
          <img src="/innova-fact-white.png" alt="innova-fact" className="h-14 w-auto" />
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tighter leading-none">
            Facturation<br />conforme aux<br />lois du <span className="text-primary">Québec.</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-sm leading-relaxed">
            TPS &amp; TVQ automatiques, numérotation unique, factures PDF par courriel et facturation récurrente.
          </p>
        </div>
        <div className="relative text-xs text-white/50 tracking-wider uppercase">TPS 5 % · TVQ 9,975 %</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="md:hidden flex items-center gap-2 mb-8">
            <img src="/innova-fact-dark.png" alt="innova-fact" className="h-9 w-auto" />
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Connexion</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-8">Accédez à votre espace de facturation.</p>

          {justRegistered && (
            <div data-testid="register-success" className="mb-6 text-sm text-emerald-800 border border-emerald-200 bg-emerald-50 rounded-sm px-3 py-2">
              Votre compte a été créé. Connectez-vous avec votre courriel et votre mot de passe.
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Courriel</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                required
                className="rounded-sm h-11"
                placeholder="vous@entreprise.ca"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mot de passe</Label>
                <button type="button" data-testid="forgot-password-link" onClick={() => navigate("/mot-de-passe-oublie")} className="text-xs text-primary hover:underline">Mot de passe oublié ?</button>
              </div>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                required
                className="rounded-sm h-11"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-sm px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-11 rounded-sm group"
            >
              {loading ? "Connexion…" : "Se connecter"}
              <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
